import { useState, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Stack, Text, Group, Badge, Button, Alert, Skeleton, Table,
  Title, Switch, Select, TextInput, NumberInput, ActionIcon,
  Divider, Paper, Tooltip, SimpleGrid, Tabs, Card, SegmentedControl,
  ScrollArea, ThemeIcon, Accordion,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import {
  IconShieldCheck, IconArrowLeft, IconTrash, IconPlus, IconBell,
  IconChartArea, IconList, IconSearch, IconArrowUp, IconArrowDown,
  IconArrowBigUpLine, IconArrowBigDownLine, IconAdjustments, IconEye, IconInfoCircle,
} from '@tabler/icons-react'
import { AreaChart } from '@mantine/charts'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { useCan } from '@/store/auth'
import { WafLoggingCard } from './WafLoggingCard'
import { FaixaDivergencia } from './codigo/FaixaDivergencia'
import { FaixaEmergencias } from './codigo/FaixaEmergencias'
import { useModoCodigo } from './codigo/useModoCodigo'
import { PainelDoCodigo } from './codigo/PainelDoCodigo'
import { PainelDeAdocao } from './codigo/PainelDeAdocao'
import { mensagemDePedido, travado } from './codigo/travas'
import type { ManagedWaf } from './WafRegistry'
import {
  getWafMetrics,
  getWafSampled,
  searchWafLogs,
  listWafAlarms,
  createWafAlarm,
  deleteWafAlarm,
  listWafRules,
  deleteWafRule,
  reorderWafRules,
  promoteWafRule,
  demoteWafRule,
  getWafRulesObservation,
  type WafAlarm,
  type CreateWafAlarmBody,
  type WafResource,
  type WafRule,
  type WafRuleObservation,
} from '@/api/waf'
import { WafRuleWizard } from './WafRuleWizard'
import { WafRuleDetailDrawer } from './WafRuleDetailDrawer'
import { WafContextSwitcher, resolveContext } from './WafContextSwitcher'
import {
  type WafContext, ruleInContext, scopeExceedsContext, hostsOutsideContext,
} from './wafContext'

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface WafIpSet {
  id: number
  ipset_id: string
  // ARN do IPSet (serializado pelo backend em GET /v1/waf/managed/{id}); usado
  // pelo wizard de criação de regra (statement IPSetReferenceStatement).
  ipset_arn?: string
  name: string
  kind: 'allow' | 'block'
  ip_version: 'IPV4' | 'IPV6'
}

interface WafDetailData extends ManagedWaf {
  ipsets: WafIpSet[]
  resources?: WafResource[]
}

interface IpSetEntries {
  addresses: string[]
  ip_version: 'IPV4' | 'IPV6'
}

interface AddEntryResponse {
  status?: 'applied' | 'pending_gmud'
  gmud_id?: number
  /** WAF do código: o gesto virou pedido do Terraform (spec 2026-10-14 D6). */
  pedido_id?: number
  stage?: string
}

// ─── API ─────────────────────────────────────────────────────────────────────

const wafDetailApi = {
  get: (wafId: string) =>
    api.get<WafDetailData>(`/v1/waf/managed/${wafId}`).then(r => r.data),

  listEntries: (wafId: string, ipsetId: string) =>
    api.get<IpSetEntries>(`/v1/waf/managed/${wafId}/ipsets/${ipsetId}/entries`).then(r => r.data),

  addEntry: (
    wafId: string,
    ipsetId: string,
    body: { kind: 'allow' | 'block'; cidr: string; ttl_minutes?: number | null; reason?: string },
  ) =>
    api.post<AddEntryResponse>(`/v1/waf/managed/${wafId}/ipsets/${ipsetId}/entries`, body).then(r => r.data),

  removeEntry: (
    wafId: string,
    ipsetId: string,
    body: { kind: 'allow' | 'block'; cidr: string },
  ) =>
    api.delete(`/v1/waf/managed/${wafId}/ipsets/${ipsetId}/entries`, { data: body }),

  patch: (wafId: string, body: Partial<Pick<ManagedWaf, 'is_managed' | 'sensitive'>>) =>
    api.patch<ManagedWaf>(`/v1/waf/managed/${wafId}`, body).then(r => r.data),
}

// ─── Sub-componente: Tabela de entradas de um IPSet ──────────────────────────

interface IpSetTableProps {
  wafId: string
  ipset: WafIpSet
  /** Stage do código quando o WAF é do Terraform (spec 2026-10-14 D6). */
  stageDoCodigo?: string | null
}

function IpSetTable({ wafId, ipset, stageDoCodigo = null }: IpSetTableProps) {
  const qc = useQueryClient()
  const can = useCan()
  const canRemove = can('waf', ipset.kind === 'allow' ? 'allow_ip:remove' : 'block_ip:remove')

  const [removeError, setRemoveError] = useState<string | null>(null)
  const [removeInfo, setRemoveInfo] = useState<string | null>(null)

  const entries = useQuery({
    queryKey: ['waf-ipset-entries', wafId, ipset.ipset_id],
    queryFn: () => wafDetailApi.listEntries(wafId, ipset.ipset_id),
  })

  const remove = useMutation({
    mutationFn: (cidr: string) =>
      wafDetailApi.removeEntry(wafId, ipset.ipset_id, { kind: ipset.kind, cidr }),
    onSuccess: (resp) => {
      setRemoveError(null)
      setRemoveInfo(mensagemDePedido(resp?.data as { pedido_id?: number; stage?: string }))
      qc.invalidateQueries({ queryKey: ['waf-ipset-entries', wafId, ipset.ipset_id] })
      if (stageDoCodigo) qc.invalidateQueries({ queryKey: ['waf-codigo', Number(wafId)] })
    },
    onError: (err: unknown) => {
      setRemoveError(formatApiError(err, 'Erro ao remover entrada.'))
    },
  })

  const addresses = entries.data?.addresses ?? []

  return (
    <Paper withBorder p="md" radius="sm">
      <Group mb="xs" justify="space-between">
        <Group gap="xs">
          <Text fw={600} size="sm">{ipset.name}</Text>
          <Badge size="sm" color={ipset.kind === 'allow' ? 'green' : 'red'} variant="light">
            {ipset.kind === 'allow' ? 'Permitir' : 'Bloquear'}
          </Badge>
          <Badge size="xs" color="gray" variant="outline">{ipset.ip_version}</Badge>
        </Group>
      </Group>

      {removeError && (
        <Alert color="red" mb="xs" withCloseButton onClose={() => setRemoveError(null)}>
          {removeError}
        </Alert>
      )}
      {removeInfo && (
        <Alert color="blue" mb="xs" withCloseButton onClose={() => setRemoveInfo(null)}>
          {removeInfo}
        </Alert>
      )}

      {entries.isError && (
        <Alert color="red" mb="xs">
          {formatApiError(entries.error, 'Erro ao carregar entradas do IPSet.')}
        </Alert>
      )}

      {entries.isLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={32} />)}
        </Stack>
      ) : addresses.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="sm">Nenhuma entrada neste IPSet.</Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>CIDR</Table.Th>
              <Table.Th>Versão IP</Table.Th>
              {canRemove && <Table.Th style={{ width: 60 }}>Ação</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {addresses.map((cidr) => (
              <Table.Tr key={cidr}>
                <Table.Td>
                  <Text size="sm" ff="monospace">{cidr}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="outline" color="gray">
                    {entries.data?.ip_version ?? ipset.ip_version}
                  </Badge>
                </Table.Td>
                {canRemove && (
                  <Table.Td>
                    <Tooltip label="Remover" position="left">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        loading={remove.isPending}
                        onClick={() => remove.mutate(cidr)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  )
}

// ─── Sub-componente: Painel de Alarmes ────────────────────────────────────────

interface AlarmsPanelProps {
  wafId: number
}

function AlarmsPanel({ wafId }: AlarmsPanelProps) {
  const qc = useQueryClient()
  const can = useCan()
  const canManageAlarm = can('waf', 'alarm:manage')

  const [alarmError, setAlarmError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Formulário criar alarme
  const [alarmRule, setAlarmRule] = useState('')
  const [alarmMetric, setAlarmMetric] = useState<string | null>('BlockedRequests')
  const [alarmThreshold, setAlarmThreshold] = useState<number | string>(100)
  const [alarmComparison, setAlarmComparison] = useState<string | null>('GreaterThanOrEqualToThreshold')
  const [alarmPeriod, setAlarmPeriod] = useState<number | string>(300)
  const [alarmName, setAlarmName] = useState('')
  const [alarmEmail, setAlarmEmail] = useState('')

  const alarmsQuery = useQuery({
    queryKey: ['waf-alarms', wafId],
    queryFn: () => listWafAlarms(wafId),
  })

  const createMutation = useMutation({
    mutationFn: (body: CreateWafAlarmBody) => createWafAlarm(wafId, body),
    onSuccess: () => {
      setAlarmError(null)
      setAlarmRule('')
      setAlarmName('')
      setAlarmEmail('')
      setAlarmThreshold(100)
      setAlarmPeriod(300)
      qc.invalidateQueries({ queryKey: ['waf-alarms', wafId] })
    },
    onError: (err: unknown) => {
      setAlarmError(formatApiError(err, 'Erro ao criar alarme.'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (alarmName: string) => deleteWafAlarm(wafId, alarmName),
    onSuccess: () => {
      setDeleteError(null)
      qc.invalidateQueries({ queryKey: ['waf-alarms', wafId] })
    },
    onError: (err: unknown) => {
      setDeleteError(formatApiError(err, 'Erro ao excluir alarme.'))
    },
  })

  function handleCreateAlarm() {
    if (!alarmRule.trim() || !alarmMetric || !alarmComparison) return
    createMutation.mutate({
      rule: alarmRule.trim(),
      metric: alarmMetric,
      threshold: typeof alarmThreshold === 'number' ? alarmThreshold : Number(alarmThreshold),
      comparison: alarmComparison,
      period: typeof alarmPeriod === 'number' ? alarmPeriod : Number(alarmPeriod),
      alarm_name: alarmName.trim() || undefined,
      sns_email: alarmEmail.trim() || undefined,
    })
  }

  const alarmStateColor = (state: string) => {
    if (state === 'OK') return 'green'
    if (state === 'ALARM') return 'red'
    return 'gray'
  }

  const alarms: WafAlarm[] = alarmsQuery.data?.alarms ?? []

  return (
    <Card withBorder radius="sm" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconBell size={16} />
          <Text fw={700} size="sm">Alarmes</Text>
        </Group>

        {deleteError && (
          <Alert color="red" withCloseButton onClose={() => setDeleteError(null)}>
            {deleteError}
          </Alert>
        )}

        {alarmsQuery.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={32} />)}
          </Stack>
        ) : alarmsQuery.isError ? (
          <Alert color="red">{formatApiError(alarmsQuery.error, 'Erro ao carregar alarmes.')}</Alert>
        ) : alarms.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="sm">Nenhum alarme configurado.</Text>
        ) : (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Estado</Table.Th>
                <Table.Th>Métrica</Table.Th>
                <Table.Th>Threshold</Table.Th>
                {canManageAlarm && <Table.Th style={{ width: 60 }}>Ação</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {alarms.map((alarm) => (
                <Table.Tr key={alarm.name}>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{alarm.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={alarmStateColor(alarm.state)} variant="light">
                      {alarm.state}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{alarm.metric}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{alarm.threshold}</Text>
                  </Table.Td>
                  {canManageAlarm && (
                    <Table.Td>
                      <Tooltip label="Excluir alarme" position="left">
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          size="sm"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(alarm.name)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Divider label="Criar alarme" labelPosition="left" />

        {alarmError && (
          <Alert color="red" withCloseButton onClose={() => setAlarmError(null)}>
            {alarmError}
          </Alert>
        )}

        <Stack gap="xs">
          <Group gap="sm" wrap="wrap" align="flex-end">
            <TextInput
              label="Regra"
              placeholder="Ex.: AWS-AWSManagedRulesCommonRuleSet"
              value={alarmRule}
              onChange={e => setAlarmRule(e.currentTarget.value)}
              disabled={!canManageAlarm}
              style={{ flex: '2 1 200px', minWidth: 180 }}
            />
            <Select
              label="Métrica"
              data={[
                { value: 'AllowedRequests', label: 'Requisições permitidas' },
                { value: 'BlockedRequests', label: 'Requisições bloqueadas' },
                { value: 'CountedRequests', label: 'Requisições contadas' },
              ]}
              value={alarmMetric}
              onChange={setAlarmMetric}
              disabled={!canManageAlarm}
              style={{ flex: '1 1 180px', minWidth: 160 }}
            />
            <NumberInput
              label="Threshold"
              value={alarmThreshold}
              onChange={setAlarmThreshold}
              min={0}
              disabled={!canManageAlarm}
              style={{ flex: '0 1 110px', minWidth: 100 }}
            />
          </Group>
          <Group gap="sm" wrap="wrap" align="flex-end">
            <Select
              label="Comparação"
              data={[
                { value: 'GreaterThanOrEqualToThreshold', label: '>= Threshold' },
                { value: 'GreaterThanThreshold', label: '> Threshold' },
                { value: 'LessThanThreshold', label: '< Threshold' },
              ]}
              value={alarmComparison}
              onChange={setAlarmComparison}
              disabled={!canManageAlarm}
              style={{ flex: '1 1 180px', minWidth: 160 }}
            />
            <NumberInput
              label="Período (segundos)"
              value={alarmPeriod}
              onChange={setAlarmPeriod}
              min={60}
              step={60}
              disabled={!canManageAlarm}
              style={{ flex: '0 1 140px', minWidth: 120 }}
            />
            <TextInput
              label="Nome do alarme (opcional)"
              placeholder="Gerado automaticamente se vazio"
              value={alarmName}
              onChange={e => setAlarmName(e.currentTarget.value)}
              disabled={!canManageAlarm}
              style={{ flex: '2 1 200px', minWidth: 180 }}
            />
            <TextInput
              label="E-mail SNS (opcional)"
              placeholder="alertas@empresa.com"
              value={alarmEmail}
              onChange={e => setAlarmEmail(e.currentTarget.value)}
              disabled={!canManageAlarm}
              style={{ flex: '2 1 200px', minWidth: 180 }}
            />
          </Group>
          <Group>
            <Button
              leftSection={<IconPlus size={14} />}
              size="xs"
              loading={createMutation.isPending}
              disabled={!canManageAlarm || !alarmRule.trim() || !alarmMetric || !alarmComparison}
              onClick={handleCreateAlarm}
            >
              Criar alarme
            </Button>
            {!canManageAlarm && (
              <Text size="xs" c="dimmed">Você não tem permissão para gerenciar alarmes.</Text>
            )}
          </Group>
        </Stack>
      </Stack>
    </Card>
  )
}

// ─── Sub-componente: Aba Métricas ─────────────────────────────────────────────

interface MetricsPanelProps {
  wafId: number
  // Banner informando que métricas CloudWatch são do Web ACL inteiro quando há
  // um contexto de domínio ativo.
  contextActive?: boolean
}

const RANGE_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hora' },
  { value: '1440', label: '24 horas' },
]

function MetricsPanel({ wafId, contextActive }: MetricsPanelProps) {
  const [rangeMinutes, setRangeMinutes] = useState('60')

  const metricsQuery = useQuery({
    queryKey: ['waf-metrics', wafId, rangeMinutes],
    queryFn: () => getWafMetrics(wafId, Number(rangeMinutes)),
    staleTime: 60_000,
  })

  const chartData = useMemo(() => {
    const raw = metricsQuery.data
    if (!raw) return []

    // Agrega todos os pontos por timestamp, somando por métrica (todas as regras)
    const tsMap = new Map<string, { Allowed: number; Blocked: number; Counted: number }>()

    for (const ruleMetrics of Object.values(raw.metrics)) {
      for (const [metricName, points] of Object.entries(ruleMetrics)) {
        const key =
          metricName === 'AllowedRequests'
            ? 'Allowed'
            : metricName === 'BlockedRequests'
            ? 'Blocked'
            : metricName === 'CountedRequests'
            ? 'Counted'
            : null

        if (!key) continue

        for (const point of points) {
          const label = new Date(point.ts).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
          const existing = tsMap.get(label) ?? { Allowed: 0, Blocked: 0, Counted: 0 }
          existing[key] = (existing[key] ?? 0) + point.value
          tsMap.set(label, existing)
        }
      }
    }

    return Array.from(tsMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ts, values]) => ({ ts, ...values }))
  }, [metricsQuery.data])

  return (
    <Stack gap="sm">
      {contextActive && (
        <Alert color="gray" variant="light">
          Métricas CloudWatch são do Web ACL inteiro e não podem ser separadas por domínio.
        </Alert>
      )}
      <Group justify="flex-end">
        <SegmentedControl
          size="xs"
          data={RANGE_OPTIONS}
          value={rangeMinutes}
          onChange={setRangeMinutes}
        />
      </Group>

      {metricsQuery.isLoading ? (
        <Skeleton height={220} />
      ) : metricsQuery.isError ? (
        <Alert color="red">{formatApiError(metricsQuery.error, 'Erro ao carregar métricas.')}</Alert>
      ) : chartData.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">
          Nenhum dado de métricas para o período selecionado.
        </Text>
      ) : (
        <AreaChart
          h={220}
          data={chartData}
          dataKey="ts"
          series={[
            { name: 'Allowed', color: 'green.5' },
            { name: 'Blocked', color: 'red.5' },
            { name: 'Counted', color: 'yellow.5' },
          ]}
          curveType="monotone"
          withDots={false}
          withTooltip
          tooltipAnimationDuration={200}
        />
      )}
    </Stack>
  )
}

// ─── Sub-componente: Aba Sampled Requests ────────────────────────────────────

interface SampledPanelProps {
  wafId: number
}

const SAMPLED_RANGE_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hora' },
]

function SampledPanel({ wafId }: SampledPanelProps) {
  const [rule, setRule] = useState('')
  const [rangeMinutes, setRangeMinutes] = useState('60')

  const sampledQuery = useQuery({
    queryKey: ['waf-sampled', wafId, rule, rangeMinutes],
    queryFn: () => getWafSampled(wafId, rule.trim() || undefined, Number(rangeMinutes)),
    staleTime: 30_000,
  })

  const actionColor = (action: string) => {
    if (action === 'BLOCK') return 'red'
    if (action === 'ALLOW') return 'green'
    if (action === 'COUNT') return 'yellow'
    return 'gray'
  }

  const items = sampledQuery.data?.samples ?? []

  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="wrap" align="flex-end">
        <TextInput
          label="Filtrar por regra"
          placeholder="Ex.: AWS-AWSManagedRulesCommonRuleSet"
          value={rule}
          onChange={e => setRule(e.currentTarget.value)}
          style={{ flex: '2 1 200px', minWidth: 180 }}
          size="xs"
        />
        <SegmentedControl
          size="xs"
          data={SAMPLED_RANGE_OPTIONS}
          value={rangeMinutes}
          onChange={setRangeMinutes}
        />
      </Group>

      {sampledQuery.isLoading ? (
        <Skeleton height={200} />
      ) : sampledQuery.isError ? (
        <Alert color="red">{formatApiError(sampledQuery.error, 'Erro ao carregar requisições amostradas.')}</Alert>
      ) : items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">
          Nenhuma requisição amostrada no período.
        </Text>
      ) : (
        <ScrollArea>
          <Table striped withTableBorder style={{ minWidth: 700 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Timestamp</Table.Th>
                <Table.Th>IP</Table.Th>
                <Table.Th>País</Table.Th>
                <Table.Th>URI</Table.Th>
                <Table.Th>Método</Table.Th>
                <Table.Th>Ação</Table.Th>
                <Table.Th>Regra</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item, idx) => (
                <Table.Tr key={idx}>
                  <Table.Td>
                    <Text size="xs" ff="monospace">
                      {new Date(item.timestamp).toLocaleString('pt-BR')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace">{item.client_ip}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{item.country}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.uri}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="outline" color="gray">{item.method}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={actionColor(item.action)} variant="light">
                      {item.action}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.rule}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Stack>
  )
}

// ─── Sub-componente: Aba Logs ─────────────────────────────────────────────────

interface LogsPanelProps {
  wafId: number
  /** Host do contexto ativo (apenas contexto de domínio); filtra a busca de logs. */
  host?: string
}

function LogsPanel({ wafId, host }: LogsPanelProps) {
  const [logQuery, setLogQuery] = useState('')
  const [logRange, setLogRange] = useState<number | string>(60)

  const searchMutation = useMutation({
    // host no key: trocar de contexto descarta o resultado anterior.
    mutationKey: ['waf-logs-search', wafId, host],
    mutationFn: () =>
      searchWafLogs(wafId, {
        query: logQuery.trim() || undefined,
        range_minutes: typeof logRange === 'number' ? logRange : Number(logRange),
        ...(host ? { host } : {}),
      }),
  })

  return (
    <Stack gap="sm">
      {host && (
        <Text size="xs" c="dimmed">
          Filtrado por: <Text span ff="monospace" inherit>{host}</Text>
        </Text>
      )}
      <Group gap="sm" wrap="wrap" align="flex-end">
        <TextInput
          label="Consulta (Logs Insights)"
          placeholder="Ex.: fields @timestamp, httpRequest.uri | limit 50"
          value={logQuery}
          onChange={e => setLogQuery(e.currentTarget.value)}
          style={{ flex: '3 1 260px', minWidth: 200 }}
          size="xs"
        />
        <NumberInput
          label="Período (minutos)"
          value={logRange}
          onChange={setLogRange}
          min={1}
          max={10080}
          size="xs"
          style={{ flex: '0 1 130px', minWidth: 110 }}
        />
        <Button
          leftSection={<IconSearch size={14} />}
          size="xs"
          loading={searchMutation.isPending}
          onClick={() => searchMutation.mutate()}
        >
          Buscar
        </Button>
      </Group>

      {searchMutation.isError && (
        <Alert color="red">
          {formatApiError(searchMutation.error, 'Erro ao buscar logs.')}
        </Alert>
      )}

      {searchMutation.data && (
        <>
          {searchMutation.data.status === 'unavailable' ? (
            // A versão anterior mandava "configure o logging no AWS WAF Console" — uma
            // instrução para sair do produto e fazer à mão. O card resolve dentro, e
            // distingue "ninguém ligou" de "esta conta não pode ligar".
            <WafLoggingCard wafId={wafId} onEnabled={() => searchMutation.mutate()} />
          ) : searchMutation.data.status === 'Failed' ||
            searchMutation.data.status === 'Cancelled' ||
            searchMutation.data.status === 'Timeout' ? (
            <Alert color="red">
              Falha na consulta de logs ({searchMutation.data.status}). Tente novamente ou verifique a query.
            </Alert>
          ) : (
            <Stack gap="sm">
              {/* Estado LIGADO do cartao: mostra o grupo e oferece DESLIGAR. O
                  `log_group` vem da propria resposta da busca — o backend ja resolvia
                  o grupo para montar a consulta, entao nao custa chamada extra. Sem
                  este trecho, ligar o log era porta de mao unica na tela. */}
              {searchMutation.data.log_group && (
                <WafLoggingCard
                  wafId={wafId}
                  logGroup={searchMutation.data.log_group}
                  onDisabled={() => searchMutation.mutate()}
                />
              )}
              <ScrollArea h={320}>
              {/* `rows`, nao `results`: a AWS devolve cada linha como LISTA de
                  {field,value}, e renderizar isso direto dava cabecalho `0,1,2` e
                  `[object Object]` em toda celula. A normalizacao mora no backend
                  (`waf_metrics_service.linhas_como_dicts`), que e o lado com teste
                  em CI — vitest nao e job. */}
              {!searchMutation.data.rows?.length ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  Nenhum resultado encontrado.
                </Text>
              ) : (
                <Table striped withTableBorder style={{ minWidth: 500 }}>
                  <Table.Thead>
                    <Table.Tr>
                      {Object.keys(searchMutation.data.rows[0]).map(col => (
                        <Table.Th key={col}>{col}</Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {searchMutation.data.rows.map((row, idx) => (
                      <Table.Tr key={idx}>
                        {Object.values(row).map((val, i) => (
                          <Table.Td key={i}>
                            <Text size="xs" ff="monospace">{String(val ?? '—')}</Text>
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              </ScrollArea>
            </Stack>
          )}
        </>
      )}

      {!searchMutation.data && !searchMutation.isPending && (
        <Text size="sm" c="dimmed" ta="center" py="lg">
          Clique em "Buscar" para consultar os logs.
        </Text>
      )}
    </Stack>
  )
}

// ─── Sub-componente: Recursos / Domínios protegidos ──────────────────────────

const RESOURCE_TYPE_LABEL: Record<WafResource['resource_type'], string> = {
  ALB:               'ALB',
  CLOUDFRONT:        'CloudFront',
  API_GATEWAY:       'API Gateway',
  APPSYNC:           'AppSync',
  COGNITO_USER_POOL: 'Cognito',
}

const RESOURCE_TYPE_COLOR: Record<WafResource['resource_type'], string> = {
  ALB:               'cyan',
  CLOUDFRONT:        'orange',
  API_GATEWAY:       'grape',
  APPSYNC:           'indigo',
  COGNITO_USER_POOL: 'teal',
}

interface ResourcesPanelProps {
  resources: WafResource[]
  ctx: WafContext
  /** Web ACL nao existe mais na AWS (derivado da resposta de /rules). */
  ausenteNaAws?: boolean
}

function ResourcesPanel({ resources, ctx, ausenteNaAws }: ResourcesPanelProps) {
  // ARN do recurso em foco (quando há contexto ativo): destaca a linha.
  const focusedArn =
    ctx.kind === 'resource' ? ctx.arn : ctx.kind === 'host' ? ctx.resourceArn : null

  // Esta lista e CACHE da descoberta. Em 12/08/2026 a tela afirmava 4 dominios
  // protegidos, de cache de 11/08, para um Web ACL ja apagado da AWS — o 500 das
  // regras avisava que algo quebrou, mas a lista de dominios mentia com cara de
  // verdade. Por isso a data viaja junto, e a ausencia vira aviso vermelho.
  const descobertoEm = resources.find(r => r.discovered_at)?.discovered_at
  const rotuloDoCache = descobertoEm ? (
    <Text size="xs" c="dimmed">
      Descoberto em {new Date(descobertoEm).toLocaleString('pt-BR')} — último inventário
      conhecido, não leitura ao vivo da AWS.
    </Text>
  ) : null

  const avisoDeAusencia = ausenteNaAws ? (
    <Alert color="red" title="Este Web ACL não existe mais na AWS">
      A linha continua no registro do ConsoleAI, mas a AWS respondeu que o recurso não
      existe. O que aparece abaixo é o último inventário conhecido, não o estado atual.
    </Alert>
  ) : null

  if (resources.length === 0) {
    return (
      <Stack gap="xs">
        {avisoDeAusencia}
        <Card withBorder radius="sm" p="md">
          <Text size="sm" c="dimmed" ta="center" py="md">
            Nenhum recurso associado — ou a role não tem permissão de listar
            (rode "Descobrir WAFs" após atualizar o StackSet).
          </Text>
        </Card>
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      {avisoDeAusencia}
      {rotuloDoCache}
    <Card withBorder radius="sm" p="md">
      <ScrollArea>
        <Table striped withTableBorder style={{ minWidth: 600 }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 130 }}>Tipo</Table.Th>
              <Table.Th>Recurso</Table.Th>
              <Table.Th>Domínios</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {resources.map((res) => {
              const focused = focusedArn != null && res.resource_arn === focusedArn
              return (
              <Table.Tr
                key={res.resource_arn}
                bg={focused ? 'var(--mantine-color-blue-light)' : undefined}
              >
                <Table.Td>
                  <Badge size="sm" variant="light" color={RESOURCE_TYPE_COLOR[res.resource_type] ?? 'gray'}>
                    {RESOURCE_TYPE_LABEL[res.resource_type] ?? res.resource_type}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="sm" fw={500}>
                        {res.resource_name || res.resource_arn}
                      </Text>
                      {focused && (
                        <Badge size="xs" variant="filled" color="blue">em foco</Badge>
                      )}
                    </Group>
                    {res.resource_dns && (
                      <Text size="xs" c="dimmed" ff="monospace">{res.resource_dns}</Text>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  {res.domains.length === 0 ? (
                    <Text size="xs" c="dimmed">—</Text>
                  ) : (
                    <Group gap={4} wrap="wrap">
                      {res.domains.map((d) => (
                        <Badge key={d} size="xs" variant="outline" color="gray">{d}</Badge>
                      ))}
                      {res.domains_partial && (
                        <Tooltip label="Lista de domínios incompleta (alguns certificados/listeners não puderam ser lidos)">
                          <Badge size="xs" variant="light" color="yellow">parcial</Badge>
                        </Tooltip>
                      )}
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Card>
    </Stack>
  )
}

// ─── Sub-componente: Regras (gestão) ──────────────────────────────────────────

const RULE_KIND_LABEL: Record<WafRule['kind'], string> = {
  managed: 'Managed group',
  rate:    'Rate-based',
  ip:      'IP set',
  geo:     'Geo',
  byte:    'String match',
  regex:   'Regex',
  json:    'JSON bruto',
  allow:   'Allow',
  other:   'Outro',
}

interface RulesPanelProps {
  wafId: number
  onCreate: () => void
  ctx: WafContext
  /** Domínios (dedup) de TODOS os recursos deste WAF — texto do aviso de regra global. */
  allDomains: string[]
  /** Stage do código quando o WAF é do Terraform (spec 2026-10-14 D7). */
  stageDoCodigo?: string | null
}

// Rótulo curto do escopo de uma regra (visão geral, sem contexto ativo).
function ruleScopeLabel(rule: WafRule): string {
  if (rule.scope_kind === 'host') return (rule.scoped_hosts ?? []).join(', ') || 'host'
  return rule.scope_kind ?? 'global'
}

// Selo de recomendação por regra (Count 7d). Cor + rótulo curto em PT-BR.
const RULE_OBS_BADGE: Record<WafRuleObservation['recommendation'], { color: string; label: string }> = {
  safe: { color: 'green', label: 'baixo' },
  review: { color: 'yellow', label: 'revisar' },
  high: { color: 'red', label: 'alto' },
  active: { color: 'gray', label: 'ativo' },
  no_data: { color: 'gray', label: 'sem dados' },
}

const OBS_TOOLTIP =
  '% do tráfego (Allowed) que casou esta regra em modo Count nos últimos 7 dias'

interface RuleObservationCellProps {
  loading: boolean
  observation?: WafRuleObservation
}

function RuleObservationCell({ loading, observation }: RuleObservationCellProps) {
  if (loading) {
    return <Skeleton h={16} w={90} />
  }
  if (!observation) {
    return <Text c="dimmed">—</Text>
  }
  const badge = RULE_OBS_BADGE[observation.recommendation]
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm">
        {observation.counted.toLocaleString('pt-BR')}
        {observation.pct != null ? ` (${observation.pct}%)` : ''}
      </Text>
      <Tooltip label={OBS_TOOLTIP} withinPortal multiline w={240}>
        <Badge size="xs" variant="light" color={badge.color}>
          {badge.label}
        </Badge>
      </Tooltip>
    </Group>
  )
}

function RulesPanel({ wafId, onCreate, ctx, allDomains, stageDoCodigo = null }: RulesPanelProps) {
  const qc = useQueryClient()
  const can = useCan()
  const canManageRule = can('waf', 'rule:manage')

  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [detailRule, setDetailRule] = useState<string | null>(null)

  const rulesQuery = useQuery({
    queryKey: ['waf-rules', wafId],
    queryFn: () => listWafRules(wafId),
  })

  // Observação (Count 7d por regra + selo) — roda em PARALELO à lista de regras,
  // sem bloquear/condicionar a renderização da tabela.
  const obsQuery = useQuery({
    queryKey: ['waf-rules-obs', wafId],
    queryFn: () => getWafRulesObservation(wafId),
  })

  // Ordem corrente das regras pelo nome (fonte para reordenação).
  const rules: WafRule[] = useMemo(() => rulesQuery.data?.rules ?? [], [rulesQuery.data])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['waf-rules', wafId] })
    if (stageDoCodigo) qc.invalidateQueries({ queryKey: ['waf-codigo', wafId] })
  }
  const travaReordem = travado('rule_reorder', stageDoCodigo)
  const travaRemover = travado('rule_remove', stageDoCodigo)
  const travaCriar = travado('rule_create', stageDoCodigo)

  const removeMutation = useMutation({
    mutationFn: (name: string) => deleteWafRule(wafId, name),
    onSuccess: () => {
      setActionError(null)
      setActionSuccess('Regra removida.')
      invalidate()
    },
    onError: (err: unknown) => {
      setActionSuccess(null)
      setActionError(formatApiError(err, 'Erro ao remover regra.'))
    },
  })

  const promoteMutation = useMutation({
    mutationFn: (rule: WafRule) => promoteWafRule(wafId, rule.name, rule.kind),
    onSuccess: (data) => {
      setActionError(null)
      if (mensagemDePedido(data)) {
        setActionSuccess(mensagemDePedido(data))
      } else if (data.status === 'pending_gmud') {
        setActionSuccess(`Promoção enviada — aguardando aprovação GMUD (#${data.gmud_id ?? '—'}).`)
      } else if (data.status === 'skipped') {
        setActionSuccess('Regra já estava ativa.')
      } else {
        setActionSuccess('Regra ativada (Block).')
      }
      invalidate()
    },
    onError: (err: unknown) => {
      setActionSuccess(null)
      setActionError(formatApiError(err, 'Erro ao promover regra.'))
    },
  })

  const demoteMutation = useMutation({
    mutationFn: (rule: WafRule) => demoteWafRule(wafId, rule.name, rule.kind),
    onSuccess: (data) => {
      setActionError(null)
      if (mensagemDePedido(data)) {
        setActionSuccess(mensagemDePedido(data))
      } else if (data.status === 'pending_gmud') {
        setActionSuccess(`Desativação enviada — aguardando aprovação GMUD (#${data.gmud_id ?? '—'}).`)
      } else if (data.status === 'skipped') {
        setActionSuccess('Regra já estava em Count.')
      } else {
        setActionSuccess('Bloqueio desativado (Count).')
      }
      invalidate()
    },
    onError: (err: unknown) => {
      setActionSuccess(null)
      setActionError(formatApiError(err, 'Erro ao desativar bloqueio da regra.'))
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (order: string[]) => reorderWafRules(wafId, order),
    onSuccess: () => {
      setActionError(null)
      setActionSuccess('Prioridades atualizadas.')
      invalidate()
    },
    onError: (err: unknown) => {
      setActionSuccess(null)
      setActionError(formatApiError(err, 'Erro ao reordenar regras.'))
    },
  })

  // Move a regra do índice `idx` em `dir` (-1 = sobe, +1 = desce) e reordena.
  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir
    if (target < 0 || target >= rules.length) return
    const order = rules.map(r => r.name)
    const tmp = order[idx]
    order[idx] = order[target]
    order[target] = tmp
    reorderMutation.mutate(order)
  }

  // Confirmação de ALCANCE: ao mutar uma regra COM um contexto de domínio ativo,
  // avisa antes de prosseguir em dois casos:
  //  (1) regra GLOBAL/gerenciada (scope_kind !== 'host') → afeta TODOS os domínios;
  //  (2) regra host-scoped cujos scoped_hosts EXCEDEM o contexto atual → afeta
  //      também domínios fora do contexto.
  // Em visão geral (ctx none) ou regra host-scoped totalmente dentro do contexto,
  // o fluxo segue inalterado (pass-through).
  function withGlobalAlcanceGuard(rule: WafRule, run: () => void) {
    if (ctx.kind === 'none') {
      run()
      return
    }
    const isGlobal = rule.scope_kind !== 'host'
    if (isGlobal) {
      const dominios = allDomains.join(', ') || '(nenhum domínio listado)'
      modals.openConfirmModal({
        title: 'Regra global',
        children: (
          <Text size="sm">
            Esta regra é global e afeta TODOS os domínios deste WAF: {dominios}. Confirmar?
          </Text>
        ),
        labels: { confirm: 'Confirmar', cancel: 'Cancelar' },
        confirmProps: { color: 'red' },
        onConfirm: run,
      })
      return
    }
    // Regra host-scoped que excede o contexto atual.
    if (scopeExceedsContext(rule, ctx)) {
      const extraDomains = hostsOutsideContext(rule, ctx).join(', ') || '(outros domínios)'
      modals.openConfirmModal({
        title: 'Alcance além do contexto',
        children: (
          <Text size="sm">
            Esta regra também se aplica a domínios fora do contexto atual: {extraDomains}.
            A alteração afetará esses domínios. Confirmar?
          </Text>
        ),
        labels: { confirm: 'Confirmar', cancel: 'Cancelar' },
        confirmProps: { color: 'red' },
        onConfirm: run,
      })
      return
    }
    // Regra host-scoped totalmente dentro do contexto: pass-through.
    run()
  }

  function handleRemove(rule: WafRule) {
    withGlobalAlcanceGuard(rule, () => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remover a regra "${rule.name}"? Esta ação altera o Web ACL na AWS.`)) return
      removeMutation.mutate(rule.name)
    })
  }

  function handlePromote(rule: WafRule) {
    withGlobalAlcanceGuard(rule, () => promoteMutation.mutate(rule))
  }

  function handleDemote(rule: WafRule) {
    withGlobalAlcanceGuard(rule, () => demoteMutation.mutate(rule))
  }

  // Move a regra `rule` (índice real `idx` na lista completa) em `dir`. Quando a
  // regra movida é global e há contexto ativo, pede confirmação de alcance.
  function handleMove(rule: WafRule, idx: number, dir: -1 | 1) {
    withGlobalAlcanceGuard(rule, () => move(idx, dir))
  }

  const busy = removeMutation.isPending || promoteMutation.isPending || demoteMutation.isPending || reorderMutation.isPending

  // Índice real de cada regra na lista completa (a reordenação é sempre global
  // ao Web ACL; o split por seção é apenas apresentacional).
  const indexOfRule = (rule: WafRule) => rules.findIndex(r => r.name === rule.name)

  const tableHead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Nome</Table.Th>
        <Table.Th style={{ width: 110 }}>Prioridade</Table.Th>
        <Table.Th style={{ width: 140 }}>Tipo</Table.Th>
        <Table.Th style={{ width: 100 }}>Ação</Table.Th>
        {ctx.kind === 'none' && <Table.Th style={{ width: 160 }}>Escopo</Table.Th>}
        <Table.Th style={{ width: 200 }}>Observação (7d)</Table.Th>
        <Table.Th>Métrica</Table.Th>
        <Table.Th style={{ width: 60 }}>Detalhes</Table.Th>
        {canManageRule && <Table.Th style={{ width: 150 }}>Ações</Table.Th>}
      </Table.Tr>
    </Table.Thead>
  )

  // Renderiza UMA linha de regra. Markup único reutilizado pelos modos
  // (visão geral × split deste-domínio/globais).
  function renderRuleRow(rule: WafRule) {
    const idx = indexOfRule(rule)
    return (
      <Table.Tr key={rule.name}>
        <Table.Td>
          <Group gap={4} wrap="nowrap">
            <Text size="sm" ff="monospace">{rule.name}</Text>
            {rule.description && (
              <Tooltip label={rule.description} multiline w={320} withArrow position="top">
                <ThemeIcon variant="subtle" color="gray" size="xs" style={{ cursor: 'help' }}>
                  <IconInfoCircle size={14} />
                </ThemeIcon>
              </Tooltip>
            )}
            {/* Selo de alcance dentro do contexto: regra deste domínio que também
                afeta outros domínios. */}
            {ctx.kind !== 'none' && scopeExceedsContext(rule, ctx) && (
              <Badge size="xs" variant="light" color="orange">também afeta outros domínios</Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{rule.priority}</Text>
        </Table.Td>
        <Table.Td>
          <Badge size="xs" variant="outline" color="gray">
            {RULE_KIND_LABEL[rule.kind] ?? rule.kind}
          </Badge>
        </Table.Td>
        <Table.Td>
          {rule.kind === 'allow' ? (
            <Badge size="xs" variant="light" color="green">Allow</Badge>
          ) : (
            <Badge
              size="xs"
              variant="light"
              color={rule.effective_action === 'active' ? 'red' : 'gray'}
            >
              {rule.effective_action === 'active' ? 'Ativo' : 'Count'}
            </Badge>
          )}
        </Table.Td>
        {ctx.kind === 'none' && (
          <Table.Td>
            <Badge size="xs" variant="outline" color="gray">{ruleScopeLabel(rule)}</Badge>
          </Table.Td>
        )}
        <Table.Td>
          <RuleObservationCell
            loading={obsQuery.isLoading}
            observation={obsQuery.data?.rules[rule.name]}
          />
        </Table.Td>
        <Table.Td>
          <Text size="xs" ff="monospace" c="dimmed">{rule.metric ?? '—'}</Text>
        </Table.Td>
        <Table.Td>
          <Tooltip label="Ver detalhes" position="top">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray"
              onClick={() => setDetailRule(rule.name)}
            >
              <IconEye size={14} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
        {canManageRule && (
          <Table.Td>
            <Group gap={4} wrap="nowrap">
              <Tooltip label={travaReordem ?? 'Subir prioridade'} position="top">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  disabled={idx <= 0 || busy || !!travaReordem}
                  onClick={() => handleMove(rule, idx, -1)}
                >
                  <IconArrowUp size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={travaReordem ?? 'Descer prioridade'} position="top">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  disabled={idx === rules.length - 1 || busy || !!travaReordem}
                  onClick={() => handleMove(rule, idx, 1)}
                >
                  <IconArrowDown size={14} />
                </ActionIcon>
              </Tooltip>
              {rule.effective_action === 'count' && (
                <Tooltip label="Promover para Block" position="top">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="orange"
                    loading={promoteMutation.isPending}
                    disabled={busy}
                    onClick={() => handlePromote(rule)}
                  >
                    <IconArrowBigUpLine size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              {rule.effective_action === 'active' && (
                <Tooltip label="Desativar bloqueio (voltar para Count)" position="top">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="gray"
                    loading={demoteMutation.isPending}
                    disabled={busy}
                    onClick={() => handleDemote(rule)}
                  >
                    <IconArrowBigDownLine size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label={travaRemover ?? 'Remover regra'} position="left">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="red"
                  loading={removeMutation.isPending}
                  disabled={busy || !!travaRemover}
                  onClick={() => handleRemove(rule)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Table.Td>
        )}
      </Table.Tr>
    )
  }

  // Partição por contexto (presentacional).
  const domainRules = ctx.kind !== 'none' ? rules.filter(r => ruleInContext(r, ctx)) : []
  const globalRules = ctx.kind !== 'none' ? rules.filter(r => !ruleInContext(r, ctx)) : []

  return (
    <Card withBorder radius="sm" p="md">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <IconAdjustments size={16} />
            <Text fw={700} size="sm">Regras</Text>
          </Group>
          {canManageRule && (
            <Tooltip label={travaCriar} disabled={!travaCriar} multiline w={280} withArrow>
              <span>
                <Button leftSection={<IconPlus size={14} />} size="xs" onClick={onCreate}
                  disabled={!!travaCriar}>
                  Criar regra
                </Button>
              </span>
            </Tooltip>
          )}
        </Group>

        {actionError && (
          <Alert color="red" withCloseButton onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert color="green" withCloseButton onClose={() => setActionSuccess(null)}>
            {actionSuccess}
          </Alert>
        )}

        {rulesQuery.isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={32} />)}
          </Stack>
        ) : rulesQuery.isError ? (
          <Alert color="red">{formatApiError(rulesQuery.error, 'Erro ao carregar regras.')}</Alert>
        ) : rules.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Nenhuma regra — ou a role não tem permissão de listar (reaplique o StackSet).
          </Text>
        ) : ctx.kind === 'none' ? (
          <ScrollArea>
            <Table striped withTableBorder style={{ minWidth: 640 }}>
              {tableHead}
              <Table.Tbody>
                {rules.map(rule => renderRuleRow(rule))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : (
          <Stack gap="md">
            {/* Regras deste domínio */}
            <Stack gap="xs">
              <Text fw={600} size="sm">Regras deste domínio</Text>
              {domainRules.length === 0 ? (
                <Text size="sm" c="dimmed" py="xs">Nenhuma regra específica deste domínio.</Text>
              ) : (
                <ScrollArea>
                  <Table striped withTableBorder style={{ minWidth: 640 }}>
                    {tableHead}
                    <Table.Tbody>
                      {domainRules.map(rule => renderRuleRow(rule))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Stack>

            {/* Regras globais compartilhadas (recolhido por padrão) */}
            <Accordion variant="separated">
              <Accordion.Item value="global">
                <Accordion.Control>
                  <Group gap="xs">
                    <Text fw={600} size="sm">Regras globais compartilhadas</Text>
                    <Badge color="orange">afeta todos os domínios</Badge>
                    <Text size="xs" c="dimmed">({globalRules.length})</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {globalRules.length === 0 ? (
                    <Text size="sm" c="dimmed" py="xs">Nenhuma regra global.</Text>
                  ) : (
                    <ScrollArea>
                      <Table striped withTableBorder style={{ minWidth: 640 }}>
                        {tableHead}
                        <Table.Tbody>
                          {globalRules.map(rule => renderRuleRow(rule))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Stack>
        )}

        {!canManageRule && rules.length > 0 && (
          <Text size="xs" c="dimmed">Você não tem permissão para gerenciar regras.</Text>
        )}
      </Stack>

      <WafRuleDetailDrawer
        wafId={wafId}
        ruleName={detailRule}
        opened={detailRule !== null}
        onClose={() => setDetailRule(null)}
      />
    </Card>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function WafDetail() {
  const { wafId } = useParams<{ wafId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const can = useCan()
  const canManage = can('waf', 'manage')

  // Formulário de adição de IP
  const [cidr, setCidr] = useState('')
  const [selectedIpsetId, setSelectedIpsetId] = useState<string | null>(null)
  const [ttlMinutes, setTtlMinutes] = useState<number | ''>('')
  const [reason, setReason] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [addSuccess, setAddSuccess] = useState<string | null>(null)

  // Curadoria
  const [patchError, setPatchError] = useState<string | null>(null)

  // Wizard de criação de regra (componente em Task 9)
  const [wizardOpen, setWizardOpen] = useState(false)

  const detail = useQuery({
    queryKey: ['waf-detail', wafId],
    queryFn: () => wafDetailApi.get(wafId!),
    enabled: !!wafId,
  })

  // O detalhe NAO chama a AWS — le so o registro local. Quem descobre que o Web ACL
  // foi apagado e a rota de regras, que desde 12/08/2026 devolve
  // `error: 'webacl_absent_in_aws'` em vez de 500. Mesma `queryKey` do RulesPanel de
  // proposito: o react-query compartilha o cache, entao isto NAO gera requisicao
  // extra — e o painel de recursos passa a poder avisar que a lista e historica.
  // Modo código (TF+GIT): só mostra a faixa quando o WAF tem vínculo com um stage.
  const modoCodigo = useModoCodigo(Number(wafId))
  const stageDoCodigo = modoCodigo.data?.vinculo?.stage_chave ?? null

  const regrasParaAusencia = useQuery({
    queryKey: ['waf-rules', wafId ? Number(wafId) : undefined],
    queryFn: () => listWafRules(Number(wafId)),
    enabled: !!wafId,
  })

  // Contexto do workspace por domínio, persistido na URL (?ctx).
  const [params, setParams] = useSearchParams()
  const ctxRaw = params.get('ctx') ?? ''
  const ctx = resolveContext(ctxRaw, detail.data?.resources ?? [])
  const setCtx = (next: string) => {
    const p = new URLSearchParams(params)
    if (next) p.set('ctx', next); else p.delete('ctx')
    setParams(p, { replace: true })
  }

  const addEntry = useMutation({
    mutationFn: (vars: {
      ipsetId: string
      kind: 'allow' | 'block'
      cidr: string
      ttl_minutes?: number | null
      reason?: string
    }) =>
      wafDetailApi.addEntry(wafId!, vars.ipsetId, {
        kind: vars.kind,
        cidr: vars.cidr,
        ttl_minutes: vars.ttl_minutes,
        reason: vars.reason,
      }),
    onSuccess: (data, vars) => {
      setAddError(null)
      const doPedido = mensagemDePedido(data)
      if (doPedido) {
        setAddSuccess(doPedido)
        qc.invalidateQueries({ queryKey: ['waf-codigo', Number(wafId)] })
      } else if (data.status === 'pending_gmud') {
        setAddSuccess(`Solicitação enviada para aprovação GMUD (#${data.gmud_id ?? '—'})`)
      } else {
        setAddSuccess('IP adicionado com sucesso.')
      }
      setCidr('')
      setSelectedIpsetId(null)
      setTtlMinutes('')
      setReason('')
      qc.invalidateQueries({ queryKey: ['waf-ipset-entries', wafId, vars.ipsetId] })
    },
    onError: (err: unknown) => {
      setAddError(formatApiError(err, 'Erro ao adicionar IP.'))
      setAddSuccess(null)
    },
  })

  const patchWaf = useMutation({
    mutationFn: (body: Partial<Pick<ManagedWaf, 'is_managed' | 'sensitive'>>) =>
      wafDetailApi.patch(wafId!, body),
    onSuccess: () => {
      setPatchError(null)
      qc.invalidateQueries({ queryKey: ['waf-detail', wafId] })
    },
    onError: (err: unknown) => {
      setPatchError(formatApiError(err, 'Erro ao atualizar configuração.'))
    },
  })

  if (!wafId) {
    return <Alert color="red">Identificador de WAF inválido.</Alert>
  }

  if (detail.isLoading) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={40} width="50%" />
        <Skeleton height={200} />
        <Skeleton height={200} />
      </Stack>
    )
  }

  if (detail.isError) {
    return (
      <Alert color="red" m="md">
        {formatApiError(detail.error, 'Erro ao carregar detalhes do WAF.')}
      </Alert>
    )
  }

  const waf = detail.data!
  const ipsets = waf.ipsets ?? []
  const wafIdNum = Number(wafId)

  // Domínios (dedup, ordenados) de TODOS os recursos deste WAF — usados no
  // wizard (sugestões) e no aviso de "regra global afeta todos os domínios".
  const allDomains = Array.from(
    new Set((waf.resources ?? []).flatMap(r => r.domains ?? [])),
  ).sort()

  // Recurso em foco (quando o contexto ativo é um recurso): usado para avisar
  // no wizard que a lista de domínios pode estar incompleta (domains_partial).
  const focusedResourceArn =
    ctx.kind === 'resource' ? ctx.arn : ctx.kind === 'host' ? ctx.resourceArn : null
  const focusedResource = focusedResourceArn
    ? (waf.resources ?? []).find(r => r.resource_arn === focusedResourceArn) ?? null
    : null

  // IPSet selecionado no formulário
  const selectedIpset = ipsets.find(ip => ip.ipset_id === selectedIpsetId) ?? null
  const selectedKind = selectedIpset?.kind ?? null

  // Verificar se pode adicionar (depende do kind do IPSet selecionado)
  const canAdd = selectedKind
    ? can('waf', selectedKind === 'allow' ? 'allow_ip:add' : 'block_ip:add')
    : false

  // Alerta GMUD: WAF sensível + IPSet allow
  const showGmudAlert = waf.sensitive && selectedKind === 'allow'

  // Opções do select de IPSet (value = ipset_id string da AWS, usado na URL)
  const ipsetOptions = ipsets.map(ip => ({
    value: ip.ipset_id,
    label: `${ip.name} (${ip.kind === 'allow' ? 'Permitir' : 'Bloquear'})`,
  }))

  function handleAddEntry() {
    if (!selectedIpset || !cidr.trim()) return
    const ttl = typeof ttlMinutes === 'number' && ttlMinutes > 0 ? ttlMinutes : null
    addEntry.mutate({
      ipsetId: selectedIpset.ipset_id,
      kind: selectedIpset.kind,
      cidr: cidr.trim(),
      ttl_minutes: ttl,
      reason: reason.trim() || undefined,
    })
  }

  return (
    <Stack gap="xl" p="md">
      {/* Navegação */}
      <Group gap="xs">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/security/waf')}
          size="sm"
        >
          Voltar ao Registro
        </Button>
      </Group>

      {/* Cabeçalho */}
      <Group align="flex-start" justify="space-between" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Group gap="sm" align="center">
            <IconShieldCheck size={22} />
            <Title order={3}>{waf.name}</Title>
          </Group>
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">Conta:</Text>
            <Text size="xs" ff="monospace" c="dimmed">{waf.account_id}</Text>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="xs" c="dimmed">Região:</Text>
            <Text size="xs" c="dimmed">{waf.region}</Text>
            <Text size="xs" c="dimmed">·</Text>
            <Text size="xs" c="dimmed">Escopo:</Text>
            <Badge size="xs" variant="light" color={waf.scope === 'CLOUDFRONT' ? 'orange' : 'cyan'}>
              {waf.scope}
            </Badge>
          </Group>
        </Stack>
        <Group gap="xs" wrap="wrap">
          {waf.sensitive && (
            <Badge color="yellow" variant="light">Sensível</Badge>
          )}
          {waf.is_managed && (
            <Badge color="blue" variant="light">Gerenciada</Badge>
          )}
          <Badge
            color={waf.default_action?.toUpperCase() === 'ALLOW' ? 'green' : 'red'}
            variant="light"
          >
            Ação padrão: {waf.default_action ?? '—'}
          </Badge>
        </Group>
      </Group>

      {(modoCodigo.data?.emergencias?.length ?? 0) > 0 && (
        <FaixaEmergencias emergencias={modoCodigo.data!.emergencias!} />
      )}
      {modoCodigo.data?.vinculo && (
        <FaixaDivergencia
          stage={modoCodigo.data.vinculo.stage_chave}
          divergencias={modoCodigo.data.divergencias}
        />
      )}
      {modoCodigo.data?.vinculo && (
        <PainelDoCodigo wafId={wafIdNum} modo={modoCodigo.data} />
      )}
      {!modoCodigo.data?.vinculo && modoCodigo.data?.adocao && (
        <PainelDeAdocao wafId={wafIdNum} modo={modoCodigo.data} />
      )}
      {modoCodigo.data?.sem_vinculo === 'acl_nao_e_do_stage' && (
        <Alert color="gray" variant="light">
          Este WAF não é o do código do stage: o plan leu outra ACL no state do módulo waf. Os
          gestos aqui mudam a AWS direto, fora do código.
        </Alert>
      )}

      {/* Seletor de contexto (workspace por domínio) */}
      <WafContextSwitcher
        resources={waf.resources ?? []}
        ctxRaw={ctxRaw}
        onChange={setCtx}
      />

      <Divider />

      {/* Seção de Recursos / Domínios protegidos */}
      <Stack gap="md">
        <Text fw={700} size="md">Recursos / Domínios protegidos</Text>
        <ResourcesPanel
          resources={waf.resources ?? []}
          ctx={ctx}
          ausenteNaAws={regrasParaAusencia.data?.error === 'webacl_absent_in_aws'}
        />
      </Stack>

      <Divider />

      {/* Seção de IPSets */}
      <Stack gap="md">
        <Text fw={700} size="md">IPSets</Text>

        {ipsets.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">
            Nenhum IPSet associado a este WAF.
          </Text>
        ) : (
          <Stack gap="md">
            {ipsets.map(ipset => (
              <IpSetTable key={ipset.id} wafId={wafId} ipset={ipset} stageDoCodigo={stageDoCodigo} />
            ))}
          </Stack>
        )}
      </Stack>

      <Divider />

      {/* Formulário Adicionar IP */}
      <Stack gap="md">
        <Text fw={700} size="md">Adicionar IP</Text>

        {addError && (
          <Alert color="red" withCloseButton onClose={() => setAddError(null)}>
            {addError}
          </Alert>
        )}

        {addSuccess && (
          <Alert color="green" withCloseButton onClose={() => setAddSuccess(null)}>
            {addSuccess}
          </Alert>
        )}

        {showGmudAlert && (
          <Alert color="yellow" icon={<IconShieldCheck size={16} />}>
            Esta ação criará uma GMUD para aprovação antes de ser aplicada.
          </Alert>
        )}

        <Group align="flex-end" gap="sm" wrap="wrap">
          <TextInput
            label="CIDR"
            placeholder="Ex.: 203.0.113.0/24"
            value={cidr}
            onChange={e => setCidr(e.currentTarget.value)}
            style={{ flex: '1 1 180px', minWidth: 180 }}
          />
          <Select
            label="IPSet"
            placeholder="Selecione o IPSet"
            data={ipsetOptions}
            value={selectedIpsetId}
            onChange={setSelectedIpsetId}
            style={{ flex: '1 1 200px', minWidth: 200 }}
            clearable
          />
          <NumberInput
            label="TTL (minutos)"
            placeholder="vazio = permanente p/ allow; block usa 24h se vazio"
            value={ttlMinutes}
            onChange={(val) => setTtlMinutes(val as number | '')}
            min={1}
            style={{ flex: '1 1 180px', minWidth: 180 }}
          />
          <TextInput
            label="Motivo"
            placeholder="Opcional"
            value={reason}
            onChange={e => setReason(e.currentTarget.value)}
            style={{ flex: '2 1 240px', minWidth: 200 }}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            loading={addEntry.isPending}
            disabled={!canAdd || !cidr.trim() || !selectedIpsetId}
            onClick={handleAddEntry}
            color={selectedKind === 'allow' ? 'green' : selectedKind === 'block' ? 'red' : undefined}
          >
            {selectedKind === 'allow'
              ? 'Permitir IP'
              : selectedKind === 'block'
              ? 'Bloquear IP'
              : 'Adicionar IP'}
          </Button>
        </Group>

        {!canAdd && selectedIpsetId && (
          <Text size="xs" c="dimmed">
            Você não tem permissão para{' '}
            {selectedKind === 'allow'
              ? 'adicionar IPs na lista de permissão'
              : 'adicionar IPs na lista de bloqueio'}.
          </Text>
        )}
      </Stack>

      {/* Seção de Regras (gestão) */}
      <Divider />
      <Stack gap="md">
        <Text fw={700} size="md">Regras</Text>
        <RulesPanel
          wafId={wafIdNum}
          onCreate={() => setWizardOpen(true)}
          ctx={ctx}
          allDomains={allDomains}
          stageDoCodigo={stageDoCodigo}
        />
      </Stack>

      <WafRuleWizard
        wafId={wafIdNum}
        opened={wizardOpen}
        onClose={() => setWizardOpen(false)}
        ipsets={ipsets}
        domains={allDomains}
        domainsPartial={focusedResource?.domains_partial ?? false}
        context={ctx}
        onCreated={() => {
          setWizardOpen(false)
          qc.invalidateQueries({ queryKey: ['waf-rules', wafIdNum] })
        }}
      />

      {/* Seção de Curadoria */}
      {canManage && (
        <>
          <Divider />
          <Stack gap="md">
            <Text fw={700} size="md">Curadoria</Text>

            {patchError && (
              <Alert color="red" withCloseButton onClose={() => setPatchError(null)}>
                {patchError}
              </Alert>
            )}

            <Group gap="xl" wrap="wrap">
              <Switch
                label="Gerenciada pelo ConsoleAI"
                description="Permite que o ConsoleAI gerencie e sincronize este WAF."
                checked={waf.is_managed}
                onChange={e => patchWaf.mutate({ is_managed: e.currentTarget.checked })}
                disabled={patchWaf.isPending}
              />
              <Switch
                label="Sensível"
                description="Alterações em IPs de allow exigirão aprovação via GMUD."
                checked={waf.sensitive}
                onChange={e => patchWaf.mutate({ sensitive: e.currentTarget.checked })}
                disabled={patchWaf.isPending}
                color="yellow"
              />
            </Group>
          </Stack>
        </>
      )}

      {/* ─── Seção de Observabilidade (Layout B) ────────────────────────────── */}
      <Divider />
      <Stack gap="md">
        <Text fw={700} size="md">Observabilidade</Text>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          {/* Esquerda — Alarmes */}
          <AlarmsPanel wafId={wafIdNum} />

          {/* Direita — Tabs: Métricas / Sampled / Logs */}
          <Card withBorder radius="sm" p="md">
            <Tabs defaultValue="metrics">
              <Tabs.List mb="md">
                <Tabs.Tab value="metrics" leftSection={<IconChartArea size={14} />}>
                  Métricas
                </Tabs.Tab>
                <Tabs.Tab value="sampled" leftSection={<IconList size={14} />}>
                  Sampled requests
                </Tabs.Tab>
                <Tabs.Tab value="logs" leftSection={<IconSearch size={14} />}>
                  Logs
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="metrics">
                <MetricsPanel wafId={wafIdNum} contextActive={ctx.kind !== 'none'} />
              </Tabs.Panel>

              <Tabs.Panel value="sampled">
                <SampledPanel wafId={wafIdNum} />
              </Tabs.Panel>

              <Tabs.Panel value="logs">
                <LogsPanel
                  wafId={wafIdNum}
                  host={ctx.kind === 'host' ? ctx.host : undefined}
                />
              </Tabs.Panel>
            </Tabs>
          </Card>
        </SimpleGrid>
      </Stack>
    </Stack>
  )
}
